/**
 * UniversalDock - Fixed bottom-right floating dock for ALL hub apps
 *
 * Provides three universal actions across every Quotech module:
 *   1. Report a Bug  – Lightweight bug/feedback form → 0x3admin API
 *   2. Teams Chat     – Opens Teams chat panel
 *   3. AI Assistant   – Lightweight AI chat or link to AI Hub
 *
 * As of PR 3d (Stack 3) the dock is REGISTRY-DRIVEN: the three built-ins
 * above are themselves expressed as `DockPanel` entries (see
 * `getBuiltinPanels` below), and consumers can inject additional panels
 * via either the `customPanels` prop or the `useDockPanels` context
 * hook. New panels are first-class citizens and render with the same
 * button + slide-out UX as the built-ins, so any hub can plug in
 * (e.g.) a Notifications, Quick Settings, or Inspector panel without
 * forking this component.
 *
 * DESIGN: macOS-dock-style pill bar pinned to bottom-right corner.
 *
 * @path packages/ui/src/components/UniversalDock.tsx
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Bug, ChevronDown, ChevronUp, EyeOff, Sparkles, Users } from 'lucide-react'
import { cn } from '../lib/utils'
import { useSortedByActionUsage } from '../lib/actionUsageSort'
import { BugReportPanel } from './universal-dock/BugReportPanel'
import { TeamsChatPanel } from './universal-dock/TeamsChatPanel'
import { AIChatPanel } from './universal-dock/AIChatPanel'
import {
  DOCK_TOGGLE_PANEL_EVENT,
  type DockTogglePanelEventDetail,
} from './universal-dock/MobileDockTriggerStrip'
import { isOverlayActive, subscribeOverlay } from './overlayChromeStore'

/** Same gate as the hub MobileDock and the legacy BottomDock: below lg
 *  the floating Report / Teams / AI bar hides and the header strip
 *  (``flex lg:hidden``) owns those three actions. A 768px gate left
 *  the bar sitting on the navigation between 768 and 1023px. */
const NARROW_BREAKPOINT_PX = 1023.98

/** localStorage keys — kept here so consumers/test rigs can clear
 *  them deterministically (e.g. the cypress harness). */
const DOCK_STORAGE_COLLAPSED = 'quotech.dock.collapsed'
const DOCK_STORAGE_HIDDEN = 'quotech.dock.hidden'

/** When the operator has collapsed the dock and then hovers it to
 *  peek at the buttons, snap back to the pill this many ms after
 *  their cursor leaves. Short enough to feel responsive, long
 *  enough that a quick mouse jiggle while moving toward a panel
 *  button doesn't dismiss the dock. Operator complaint that drove
 *  this: 2026-05-04, "report / teams AI chat overlays this … just
 *  have it auto collapse on hover and so on". */
const DOCK_HOVER_RECOLLAPSE_MS = 800

/**
 * BULLETPROOF positioning style. We render the dock through a body
 * portal AND apply inline styles so:
 *
 *   1. NO ancestor `transform` / `filter` / `will-change` / `contain`
 *      can hijack the containing block (these were silently anchoring
 *      the dock to the sidebar's left edge on the SAM jobs route on
 *      2026-05-02 — confirmed via screenshot from the user).
 *   2. Inline styles win over Tailwind class weight + survive any
 *      JIT purge that drops `right-6` from the production CSS.
 *   3. The z-index sits above ordinary page chrome (MobileDock z-30,
 *      bottom bars, sticky headers) but BELOW modal scrims (z-50):
 *      while a modal (Link to Job, attachment preview, settings…) is
 *      open, the dock must be dimmed under the scrim like everything
 *      else — it previously sat at z-60 "so the dock is always
 *      reachable", which meant the pill stayed clickable on top of
 *      every dimmed modal backdrop (UX audit 2026-06, Layout #2).
 *
 * The `right` calc reads `--quotech-kanban-drawer-w`, published on
 * `<html>` by `CardDetailDrawer` whenever its drawer is open in
 * either overlay or pinned mode. When the var is unset the calc
 * resolves to 24px and the dock keeps its historical position;
 * when the drawer is open the dock slides left by the drawer width
 * so the pill stops overlapping the panel's bottom-right footer
 * (operator complaint 2026-05-05). Smooth `right` transition
 * gives the slide a 200ms ease so it never jumps abruptly.
 *
 * The `bottom` calc reads `--mobile-dock-h`, published on `<html>`
 * by the per-tenant `MobileDock.tsx` whenever the narrow viewport
 * dock is mounted (96px when visible, 0px when hidden). On desktop
 * the var is unset and the calc resolves to 0 — the dock keeps its
 * historical position flush with the viewport bottom. On narrow
 * with the MobileDock visible, the pill lifts to sit ABOVE the dock
 * strip instead of landing on top of a hub tile (operator complaint
 * 2026-05-20: "that arrow in mobile view is overlapping another
 * arrow for teams"). `bottom` shares the same 200ms ease as `right`
 * so the lift is consistent.
 *
 * `--quotech-page-bottom-bar-h` is the generic third term, for any page
 * that ends in a full-width interactive strip. The two vars above it are
 * each named after one surface, so the fourth surface to dock a bar had
 * nothing to publish and lost: the Jobs board's foot metrics bar put its
 * health bands and its Customise metrics gear in the bottom-right corner,
 * which is where this dock sits, and the dock ate the clicks. Playwright
 * reported it as "subtree intercepts pointer events" and timed out - an
 * operator just finds two controls that do nothing. Publish the height of
 * a bottom-docked bar into this var and the dock lifts clear of it; the
 * var stays generic so the fifth surface does not need a fourth term.
 */
const DOCK_FIXED_STYLE: CSSProperties = {
  position: 'fixed',
  // Lift above the mobile dock, the Email Hub's bottom nav bar (published as
  // `--email-hub-bottom-bar-h` by EmailLayout when the nav is docked to the
  // bottom), and any page-level bottom bar, so neither the collapsed arrow
  // nor the open bar lands on top of another surface's controls.
  bottom:
    'calc(var(--mobile-dock-h, 0px) + var(--email-hub-bottom-bar-h, 0px)'
    + ' + var(--quotech-page-bottom-bar-h, 0px))',
  right: 'calc(24px + var(--quotech-kanban-drawer-w, 0px))',
  zIndex: 45,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 0,
  pointerEvents: 'none', // children re-enable
  transition: 'right 200ms ease, bottom 200ms ease',
}

const DOCK_BAR_INLINE_STYLE: CSSProperties = {
  pointerEvents: 'auto',
}

// Re-export types for public API
export type {
  UniversalDockProps,
  DockAction,
  DockApiClient,
  DockPanel,
} from './universal-dock/types'
import type { UniversalDockProps, DockPanel } from './universal-dock/types'

const ACTION_COLORS: Record<string, string> = {
  teal: 'bg-teal-500 hover:bg-teal-600 text-white shadow-md shadow-teal-500/20',
  blue: 'bg-blue-500 hover:bg-blue-600 text-white shadow-md shadow-blue-500/20',
  amber: 'bg-amber-500 hover:bg-amber-600 text-white shadow-md shadow-amber-500/20',
  purple: 'bg-purple-500 hover:bg-purple-600 text-white shadow-md shadow-purple-500/20',
  rose: 'bg-rose-500 hover:bg-rose-600 text-white shadow-md shadow-rose-500/20',
}

/**
 * Active-state highlight palette for panel buttons. Each entry is the
 * Tailwind class string applied when the panel is the currently-open one.
 * Keys match `DockPanel.activeColor` exactly — extending the union there
 * requires adding the corresponding entry here.
 */
const PANEL_ACTIVE_COLORS: Record<NonNullable<DockPanel['activeColor']>, string> = {
  rose: 'bg-rose-100 dark:bg-rose-900/30 text-rose-600',
  violet: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600',
  teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600',
  amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600',
  blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600',
  purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600',
}

const PANEL_BUTTON_IDLE = 'hover:bg-muted text-muted-foreground'

/**
 * The three permanent built-in panels. Expressed as DockPanel entries
 * so they share the same render path as consumer-registered panels.
 * Captures `user` + `aiHubUrl` from the dock props via closure.
 */
function getBuiltinPanels(
  user: UniversalDockProps['user'],
  aiHubUrl: string,
): DockPanel[] {
  return [
    {
      id: 'bug',
      label: 'Report',
      icon: <Bug className="w-3.5 h-3.5" />,
      activeColor: 'rose',
      render: ({ isOpen, onClose }) => (
        <BugReportPanel isOpen={isOpen} onClose={onClose} user={user} />
      ),
    },
    {
      id: 'teams',
      label: 'Teams',
      icon: <Users className="w-3.5 h-3.5" />,
      activeColor: 'violet',
      render: ({ isOpen, onClose }) => (
        <TeamsChatPanel isOpen={isOpen} onClose={onClose} />
      ),
    },
    {
      id: 'ai',
      label: 'AI Chat',
      icon: <Sparkles className="w-3.5 h-3.5" />,
      activeColor: 'teal',
      render: ({ isOpen, onClose }) => (
        <AIChatPanel isOpen={isOpen} onClose={onClose} aiHubUrl={aiHubUrl} />
      ),
    },
  ]
}

interface PanelButtonProps {
  panel: DockPanel
  isActive: boolean
  onToggle: () => void
}

function PanelButton({ panel, isActive, onToggle }: PanelButtonProps) {
  const activeClass =
    PANEL_ACTIVE_COLORS[panel.activeColor ?? 'teal'] ?? PANEL_ACTIVE_COLORS.teal
  const showLabelOnMobile = panel.hideLabelOnMobile === false

  return (
    <button
      onClick={onToggle}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
        isActive ? activeClass : PANEL_BUTTON_IDLE,
      )}
    >
      {panel.icon}
      <span className={showLabelOnMobile ? '' : 'hidden sm:inline'}>{panel.label}</span>
    </button>
  )
}

export function UniversalDock({
  apiClient,
  user,
  orgId,
  hidden = false,
  customActions = [],
  customPanels = [],
  aiHubUrl = 'https://dashboard.quotech.ai/ai-hub',
}: UniversalDockProps) {
  // `apiClient` and `orgId` are forwarded to panels in a future iteration
  // (e.g. when BugReportPanel switches from its hard-coded fetch to the
  // shared adapter). They're declared here so callers can wire them now.
  void apiClient
  void orgId

  const [activePanel, setActivePanel] = useState<string | null>(null)

  // Operator-controlled visibility. Two persisted layers + one
  // ephemeral one:
  //   - `collapsed` (persisted): operator preference. When true,
  //     the dock renders as a single round pill anchored to the
  //     corner. Hovering the pill expands the bar TEMPORARILY
  //     (without flipping the persisted preference) so they can
  //     peek at the buttons. This is the "auto collapse on hover"
  //     behaviour the operator asked for on 2026-05-04.
  //   - `userHidden` (persisted): operator hit "Hide". The dock
  //     disappears entirely, leaving only a small edge tab they
  //     can click to bring it back. Reversible in one click.
  //   - `peeking` (ephemeral): true while the operator hovers
  //     the collapsed pill or has a panel open. Drives the
  //     temporary expansion when `collapsed=true`.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(DOCK_STORAGE_COLLAPSED) === '1'
  })
  const [userHidden, setUserHidden] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(DOCK_STORAGE_HIDDEN) === '1'
  })
  const [peeking, setPeeking] = useState(false)
  useEffect(() => {
    try { window.localStorage.setItem(DOCK_STORAGE_COLLAPSED, collapsed ? '1' : '0') } catch {}
  }, [collapsed])
  useEffect(() => {
    try { window.localStorage.setItem(DOCK_STORAGE_HIDDEN, userHidden ? '1' : '0') } catch {}
  }, [userHidden])

  // Operator complaint 2026-05-19: "see how the bottom of this with
  // feedback teams and ai chat overlaps you can even hide them
  // here.. I thought we implemented this universally across
  // everything ages ago". The legacy coupling (a `--quotech-kanban-
  // drawer-w` CSS var that SHIFTS the dock left) only moved the
  // pill out of the way when the drawer was narrower than the
  // viewport; the moment a pinned rail occupied >50% of the
  // viewport, the pill ended up sitting INSIDE the rail's content
  // region anyway. This subscription auto-hides the dock entirely
  // whenever ANY component has registered with `acquireOverlay()`
  // (today: CardDetailDrawer; future: EmailPreviewPane once it
  // migrates to the same shell). One mechanism, every consumer.
  const [overlayHidden, setOverlayHidden] = useState<boolean>(isOverlayActive)
  useEffect(() => subscribeOverlay(setOverlayHidden), [])

  // Narrow-viewport detection — operator complaint 2026-05-24
  // ("in mobile view the teams bug ai make it go in the top nav..
  // as that space is empty"). On narrow we SUPPRESS the floating
  // bar entirely and let the master Header's
  // ``<MobileDockTriggerStrip />`` own the affordance; the panel
  // bodies still mount via the ``PanelBodySlot`` chain below so
  // each panel's full-screen overlay continues to work from the
  // header trigger.
  //
  // We listen to a single ``window.matchMedia`` change rather than
  // resizing the entire component on every window-resize event
  // (cheaper + survives orientation flips). The breakpoint mirrors
  // the trigger-strip's ``lg:hidden`` gate so the two surfaces
  // toggle in lockstep with the bottom navigation.
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(`(max-width: ${NARROW_BREAKPOINT_PX}px)`).matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia(`(max-width: ${NARROW_BREAKPOINT_PX}px)`)
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches)
    // Older Safari only supports addListener (pre-MQL v2).
    if (mql.addEventListener) {
      mql.addEventListener('change', handler)
      return () => mql.removeEventListener('change', handler)
    }
    mql.addListener(handler)
    return () => mql.removeListener(handler)
  }, [])

  // Hover-out re-collapse timer. Only meaningful when the
  // PERSISTED preference is collapsed and the operator is
  // peeking. We never auto-collapse a dock that the operator
  // hasn't already chosen to collapse — that would be a
  // surprise, and the operator complaint was specifically
  // about the dock OVERLAYING content, not about it appearing
  // at all.
  const peekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelPeekTimer = useCallback(() => {
    if (peekTimerRef.current) {
      clearTimeout(peekTimerRef.current)
      peekTimerRef.current = null
    }
  }, [])
  const armPeekTimer = useCallback(() => {
    cancelPeekTimer()
    peekTimerRef.current = setTimeout(() => {
      setPeeking(false)
      peekTimerRef.current = null
    }, DOCK_HOVER_RECOLLAPSE_MS)
  }, [cancelPeekTimer])
  useEffect(() => () => cancelPeekTimer(), [cancelPeekTimer])

  // Built-in panels are rebuilt only when their captured deps change so
  // that the closure pointing at fresh `user` / `aiHubUrl` is stable.
  const builtinPanels = useMemo(
    () => getBuiltinPanels(user, aiHubUrl),
    [user, aiHubUrl],
  )

  // De-duplicate by id: a custom panel with the same id as a built-in
  // overrides the built-in (allows hubs to swap, e.g., a richer AI panel).
  const panels = useMemo(() => {
    const byId = new Map<string, DockPanel>()
    for (const p of [...builtinPanels, ...customPanels]) {
      byId.set(p.id, p)
    }
    return Array.from(byId.values())
  }, [builtinPanels, customPanels])

  const { sorted: sortedCustomActions, onActionUsed: trackDockAction } =
    useSortedByActionUsage('quotech.dock.actions', customActions, (a) => a.id)

  const builtinIds = useMemo(
    () => new Set(builtinPanels.map((p) => p.id)),
    [builtinPanels],
  )
  const builtinRenderedRaw = useMemo(
    () => panels.filter((p) => builtinIds.has(p.id)),
    [panels, builtinIds],
  )
  const customRenderedRaw = useMemo(
    () => panels.filter((p) => !builtinIds.has(p.id)),
    [panels, builtinIds],
  )
  const { sorted: builtinRendered, onActionUsed: trackBuiltinPanel } =
    useSortedByActionUsage(
      'quotech.dock.panels.builtin',
      builtinRenderedRaw,
      (p) => p.id,
    )
  const { sorted: customRendered, onActionUsed: trackCustomPanel } =
    useSortedByActionUsage(
      'quotech.dock.panels.custom',
      customRenderedRaw,
      (p) => p.id,
    )

  const handleToggle = (id: string) => {
    if (builtinIds.has(id)) trackBuiltinPanel(id)
    else trackCustomPanel(id)
    setActivePanel((prev) => (prev === id ? null : id))
  }
  const handleClose = () => setActivePanel(null)

  // Cross-surface event bus — listen for ``MobileDockTriggerStrip``
  // (mounted in the master Header on narrow) dispatching a panel
  // toggle. We rely on identical id strings ('bug' / 'teams' / 'ai')
  // matching ``getBuiltinPanels``. Custom panels can also be opened
  // by dispatching the same event with a custom id; we don't validate
  // here because the activePanel state machine handles unknown ids
  // gracefully (no panel matches → no body renders → noop). Kept
  // outside of the narrow-gate so a user-resizes-while-panel-open
  // flow continues to work even if the dispatch races the resize.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<DockTogglePanelEventDetail>).detail
      if (!detail?.panelId) return
      if (builtinIds.has(detail.panelId)) trackBuiltinPanel(detail.panelId)
      else trackCustomPanel(detail.panelId)
      setActivePanel((prev) => (prev === detail.panelId ? null : detail.panelId))
    }
    window.addEventListener(DOCK_TOGGLE_PANEL_EVENT, handler as EventListener)
    return () => window.removeEventListener(DOCK_TOGGLE_PANEL_EVENT, handler as EventListener)
  }, [builtinIds, trackBuiltinPanel, trackCustomPanel])

  // Hooks-rule discipline: portalTarget's useState + useEffect MUST
  // be declared BEFORE the `if (hidden || overlayHidden) return null`
  // guard below. When `hidden` flips (operator toggles dock visibility
  // via the AppShell's hidden prop, e.g. on a /print/* route or in
  // kiosk mode) or `overlayHidden` flips (overlay subscribe fires
  // late), the hook count would otherwise jump 8 → 10 between renders
  // and React would throw error #300 ("Rendered fewer hooks than
  // expected"). Hoisting both portal hooks keeps the hook count
  // constant; the guard still bails right after so we render null
  // when the dock is hidden.
  //
  // Render via portal to document.body so the dock is anchored to the
  // viewport (NOT to an ancestor that may have a transform/filter/
  // backdrop-filter and accidentally become its containing block).
  // Until the portal target exists (SSR / first paint), render nothing.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalTarget(document.body)
    }
  }, [])

  if (hidden || overlayHidden) return null
  if (!portalTarget) return null

  // Pin the dock open: clears collapsed + hidden + cancels any
  // pending re-collapse. Used by the edge tab and "Show" affordances.
  const expandPersistent = () => {
    cancelPeekTimer()
    setPeeking(false)
    setCollapsed(false)
    setUserHidden(false)
  }
  // Temporary peek: keep the persisted collapsed preference but
  // show the bar while hovering. Mouse-leave re-arms the timer.
  const startPeek = () => {
    cancelPeekTimer()
    setPeeking(true)
  }
  const endPeek = () => {
    if (!activePanel) armPeekTimer()
  }
  const collapseNow = () => {
    cancelPeekTimer()
    setActivePanel(null)
    setPeeking(false)
    setCollapsed(true)
  }
  const hideForSession = () => {
    cancelPeekTimer()
    setActivePanel(null)
    setPeeking(false)
    setUserHidden(true)
  }
  // Effective expansion = persisted preference OR active peek OR
  // a panel is open. Panels open via keyboard shortcut from a
  // collapsed dock should still show their button highlighted.
  const effectiveExpanded = !collapsed || peeking || !!activePanel

  // Narrow viewport — render ONLY the panel slots; the trigger
  // strip in the master Header owns the buttons. We keep the panel
  // bodies mounted (each panel handles its own ``isOpen=false``
  // render, typically returning null) so that a header-button
  // dispatch reliably reveals the panel without any mount race.
  if (isNarrow) {
    return createPortal(
      <>
        {panels.map((panel) => (
          <PanelBodySlot
            key={panel.id}
            panel={panel}
            isOpen={activePanel === panel.id}
            onClose={handleClose}
          />
        ))}
      </>,
      portalTarget,
    )
  }

  // Hidden state — render only a tiny edge tab in the corner the
  // operator can hover to bring the dock back. Acts like a window
  // shade pull tab. We deliberately keep it visible (not 0px) so
  // the dock is always RECOVERABLE; a permanent hide would
  // strand operators who don't know the keyboard escape route.
  if (userHidden) {
    return createPortal(
      <div data-quotech-dock-hidden style={DOCK_FIXED_STYLE} aria-label="Quotech dock — hidden">
        <button
          type="button"
          onClick={expandPersistent}
          aria-label="Show Quotech dock"
          title="Show dock (Report · Teams · AI Chat)"
          className="pointer-events-auto mb-1 mr-0 flex items-center gap-1 rounded-l-md rounded-r-none border border-border border-r-0 bg-card/90 px-1.5 py-1 text-[10.5px] font-medium text-muted-foreground shadow hover:bg-muted hover:text-foreground backdrop-blur"
          style={{ pointerEvents: 'auto' }}
        >
          <ChevronUp className="h-3 w-3" />
        </button>
      </div>,
      portalTarget,
    )
  }

  // Collapsed state — single round pill anchored to the corner.
  // Hovering the pill peeks the bar; clicking pins it open. Right-
  // click hides for the session. We render the pill INSTEAD of the
  // bar only when there's no active panel and no peek in progress;
  // otherwise the full dockTree below renders so the operator sees
  // their open panel + the buttons it owns.
  if (collapsed && !effectiveExpanded) {
    return createPortal(
      <div
        data-quotech-dock-collapsed
        style={DOCK_FIXED_STYLE}
        aria-label="Quotech dock — collapsed"
      >
        <button
          type="button"
          onClick={expandPersistent}
          onMouseEnter={startPeek}
          onContextMenu={(e) => {
            e.preventDefault()
            hideForSession()
          }}
          aria-label="Expand Quotech dock"
          title="Quotech tools — hover to peek · click to pin open · right-click to hide"
          className="pointer-events-auto mb-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card/90 text-muted-foreground shadow-lg backdrop-blur-sm transition-all hover:scale-110 hover:bg-muted hover:text-foreground"
          style={{ pointerEvents: 'auto' }}
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      </div>,
      portalTarget,
    )
  }

  const dockTree = (
    <div
      data-quotech-dock
      aria-label="Quotech bottom dock"
      style={DOCK_FIXED_STYLE}
    >
      {/* Panel bodies — each is responsible for its own isOpen=false render */}
      {panels.map((panel) => (
        <PanelBodySlot
          key={panel.id}
          panel={panel}
          isOpen={activePanel === panel.id}
          onClose={handleClose}
        />
      ))}

      {/* Dock Bar — hover handlers live HERE (not on the outer
          container) because the outer container has
          `pointer-events: none` to avoid hijacking page clicks.
          Children with `pointer-events: auto` receive events
          normally, so the bar itself is the right hook point
          for the peek state machine. When the persisted state is
          `collapsed`, hovering keeps the bar visible; leaving
          arms the re-collapse timer. */}
      <div
        style={DOCK_BAR_INLINE_STYLE}
        onMouseEnter={collapsed ? startPeek : cancelPeekTimer}
        onMouseLeave={collapsed ? endPeek : undefined}
        className="flex items-center gap-2 p-2 bg-card/90 backdrop-blur-sm border border-border border-b-0 rounded-t-xl shadow-lg"
      >
        {/* Custom page actions (one-shot click handlers, not panels) */}
        {sortedCustomActions.length > 0 && (
          <>
            {sortedCustomActions.map((action) => (
              <button
                key={action.id}
                onClick={() => {
                  trackDockAction(action.id)
                  action.onClick()
                }}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                  ACTION_COLORS[action.color || 'blue'],
                )}
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
            ))}
            <div className="w-px h-5 bg-border" />
          </>
        )}

        {/* Built-in panel buttons — Report / Teams / AI */}
        {builtinRendered.map((panel, idx) => (
          <DockBarSegment
            key={panel.id}
            panel={panel}
            isActive={activePanel === panel.id}
            onToggle={() => handleToggle(panel.id)}
            withDivider={idx < builtinRendered.length - 1 || customRendered.length > 0}
          />
        ))}

        {/* Consumer-registered panels (rendered to the right of the built-ins) */}
        {customRendered.map((panel, idx) => (
          <DockBarSegment
            key={panel.id}
            panel={panel}
            isActive={activePanel === panel.id}
            onToggle={() => handleToggle(panel.id)}
            withDivider={idx < customRendered.length - 1}
          />
        ))}

        {/* Operator controls — collapse to pill (frees the corner
            for page content) and hide for the rest of the session.
            Sits flush right with a thin divider so it reads as
            chrome, not a panel. Both states are reversible: hover
            the pill to expand, click the edge tab to un-hide. */}
        <div className="w-px h-5 bg-border" />
        <button
          type="button"
          onClick={collapseNow}
          aria-label="Collapse Quotech dock"
          title="Collapse dock (auto-collapses after a few seconds of inactivity)"
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={hideForSession}
          aria-label="Hide Quotech dock"
          title="Hide dock — re-open from the corner tab"
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <EyeOff className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )

  return createPortal(dockTree, portalTarget)
}

interface DockBarSegmentProps extends PanelButtonProps {
  /** Render a thin vertical divider after the button (for visual rhythm) */
  withDivider: boolean
}

function DockBarSegment({ panel, isActive, onToggle, withDivider }: DockBarSegmentProps) {
  return (
    <>
      <PanelButton panel={panel} isActive={isActive} onToggle={onToggle} />
      {withDivider && <div className="w-px h-5 bg-border" />}
    </>
  )
}

interface PanelBodySlotProps {
  panel: DockPanel
  isOpen: boolean
  onClose: () => void
}

function PanelBodySlot({ panel, isOpen, onClose }: PanelBodySlotProps) {
  // Defer to the panel's own render — it decides what to do when
  // `isOpen=false` (typically returning null for an exit animation or
  // immediate unmount). This matches the historical contract of the
  // three built-in panels.
  return <>{panel.render({ isOpen, onClose })}</>
}

// Cast to ReactNode for export-default compatibility with consumers that
// import the dock as a default export.
export default UniversalDock as (props: UniversalDockProps) => ReactNode
